import React from "react";

import { Box, Card, CardContent, Typography, Avatar } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import ActionsList from "./ActionsList";
import InsightsModalManager, {
  ModalType,
} from "../../../../common/ModalManager";
import { toHHMMSS } from "../../../../../utils/formatTIme";
import { Quote as QuoteType } from "../../../services/episode.service";
import { Category } from "../../../../common/types";

export type QuoteObject = {
  speaker: string;
  quote: string;
  timestamp: string;
};

interface QuoteProps {
  quoteObject: QuoteObject;
  quotes: QuoteType[];
  refetch: () => void;
  category: Category;
}

export default function Quote({
  quoteObject,
  quotes,
  refetch,
  category,
}: QuoteProps) {
  const [openInsightsModalManager, setOpenInsightsModalManager] =
    React.useState(false);
  const handleOpenInsightsModalManager = () =>
    setOpenInsightsModalManager(true);
  const handleCopy = () => navigator.clipboard.writeText(quoteObject.quote);
  const speakerInitial = quoteObject.speaker?.[0]?.toUpperCase() || "?";

  return (
    <Box key={quoteObject.timestamp} sx={{ width: "100%" }}>
      <InsightsModalManager
        open={openInsightsModalManager}
        setOpen={setOpenInsightsModalManager}
        modalType={ModalType.QUOTES}
        modalTitle="Edit Quote"
        objectValue={quoteObject}
        values={quotes}
        refetch={refetch}
        category={category}
      />
      <Card
        sx={{
          width: "100%",
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          borderRadius: 2,
          p: 2,
          mb: 2,
          border: "none",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Top Row: Avatar, Speaker, Timestamp, Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "#1976D2",
                  width: 36,
                  height: 36,
                  fontWeight: 700,
                  fontSize: 18,
                  mr: 1.5,
                }}
              >
                {speakerInitial}
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontSize: 16 }}
              >
                {quoteObject.speaker}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: "#888", mr: 0.5 }} />
              <Typography
                variant="caption"
                sx={{ color: "#888", fontWeight: 500, mr: 1 }}
              >
                {toHHMMSS(quoteObject.timestamp)}
              </Typography>
              <ActionsList
                handleOpenInsightsModalManager={handleOpenInsightsModalManager}
                handleCopy={handleCopy}
              />
            </Box>
          </Box>
          {/* Quote Text */}
          <Typography
            variant="body1"
            sx={{
              color: "#222",
              fontSize: 15,
              fontWeight: 400,
              lineHeight: 1.6,
              mt: 1.5,
            }}
          >
            {quoteObject.quote}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
